/**
 * Control de Kits — módulo autocontenido (migrado de Replit).
 *
 * Se monta como tab "Kits" dentro de Oficinas y en la página standalone de
 * Office Manager. Reemplaza el Layout+router del original por un selector de
 * "Clapbase Activa" + tabs internas; la autenticación es la de la plataforma.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/contexts/SharedAuthContext';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { KitsDashboard } from './KitsDashboard';
import { KitsMovements } from './KitsMovements';
import { KitsOrders } from './KitsOrders';
import { KitsTransfers } from './KitsTransfers';
import { KitsAdmin } from './KitsAdmin';
import type { KitOfficeSummary } from '@/../../shared/types';
import { useTranslation } from 'react-i18next';

interface KitsContextValue {
  activeOfficeId: string | null;
  setActiveOfficeId: (id: string) => void;
  offices: KitOfficeSummary[];
  isGlobal: boolean;
  isAdmin: boolean;
}

const KitsContext = createContext<KitsContextValue | null>(null);

export function useKits(): KitsContextValue {
  const ctx = useContext(KitsContext);
  if (!ctx) throw new Error('useKits debe usarse dentro de KitsModule');
  return ctx;
}

const ACTIVE_OFFICE_KEY = 'kits.activeOfficeId';

export function KitsModule() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = (user?.roles ?? []).includes('admin');

  const { data: access } = trpc.kits.getMyAccess.useQuery(undefined, { staleTime: 5 * 60 * 1000 });
  const { data: offices, isLoading } = trpc.kits.listOffices.useQuery(undefined, {
    enabled: !!access?.canView,
  });

  const [activeOfficeId, setActiveOfficeIdState] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_OFFICE_KEY)
  );

  const setActiveOfficeId = (id: string) => {
    localStorage.setItem(ACTIVE_OFFICE_KEY, id);
    setActiveOfficeIdState(id);
  };

  // Validar la oficina activa contra las accesibles (default: la primera)
  useEffect(() => {
    if (!offices || offices.length === 0) return;
    if (!activeOfficeId || !offices.some((o) => o.officeId === activeOfficeId)) {
      setActiveOfficeId(offices[0].officeId);
    }
  }, [offices, activeOfficeId]);

  const value = useMemo<KitsContextValue>(
    () => ({
      activeOfficeId,
      setActiveOfficeId,
      offices: offices ?? [],
      isGlobal: access?.isGlobal ?? false,
      isAdmin,
    }),
    [activeOfficeId, offices, access?.isGlobal, isAdmin]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  if (!offices || offices.length === 0) {
    return (
      <div className="p-12 text-center text-muted-foreground">
        {isAdmin
          ? t('kits.module.todavia_no_hay_clapbases')
          : t('kits.module.sin_clapbases_asignadas')}
      </div>
    );
  }

  return (
    <KitsContext.Provider value={value}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">{t('kits.module.clapbase_activa')}</span>
            <Select value={activeOfficeId ?? undefined} onValueChange={setActiveOfficeId}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                {offices.map((o) => (
                  <SelectItem key={o.officeId} value={o.officeId}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs defaultValue="dashboard">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="dashboard">{t('kits.common.dashboard')}</TabsTrigger>
            <TabsTrigger value="movimientos">{t('kits.common.movimientos')}</TabsTrigger>
            <TabsTrigger value="pedidos">{t('kits.common.pedidos')}</TabsTrigger>
            <TabsTrigger value="transferencias">{t('kits.common.transferencias')}</TabsTrigger>
            {isAdmin && <TabsTrigger value="admin">{t('kits.module.administracion')}</TabsTrigger>}
          </TabsList>
          <TabsContent value="dashboard" className="mt-4">
            <KitsDashboard />
          </TabsContent>
          <TabsContent value="movimientos" className="mt-4">
            <KitsMovements />
          </TabsContent>
          <TabsContent value="pedidos" className="mt-4">
            <KitsOrders />
          </TabsContent>
          <TabsContent value="transferencias" className="mt-4">
            <KitsTransfers />
          </TabsContent>
          {isAdmin && (
            <TabsContent value="admin" className="mt-4">
              <KitsAdmin />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </KitsContext.Provider>
  );
}
